import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Cpu,
  Database,
  HardDrive,
  XCircle,
} from 'lucide-react';
import { fetchSystemzustand, type Systemzustand } from '@/api/betrieb.api';
import { bytes, dauer, zahl, zeitpunkt } from '@/components/format';

// ─── Systemzustand ──────────────────────────────────────────────────────────
//
// Die Frage, die diese Seite beantwortet: läuft alles, und wenn nicht, was
// nicht? Deshalb steht Auffälliges oben und in Farbe, der Rest ruhig darunter.
//
// Die Zahlen beziehen sich auf den Prozess der Betriebs-API. Die Anwendung
// läuft in einem eigenen Container; ihre Laufzeit steht hier nicht.

export function SystemPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'system'],
    queryFn: fetchSystemzustand,
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return <div className="p-4 text-sm text-slate-500 sm:p-6">Systemzustand wird geprüft…</div>;
  }

  if (isError || !data) {
    return (
      <div className="p-4 sm:p-6">
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error instanceof Error ? error.message : 'Zustand konnte nicht ermittelt werden.'}
        </p>
      </div>
    );
  }

  const probleme = sammleProbleme(data);

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Systemzustand</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Aktualisiert sich alle 30 Sekunden. Die Laufzeitwerte gelten für die
          Betriebs-API.
        </p>
      </header>

      {/* Zuerst, was Aufmerksamkeit braucht */}
      {probleme.length > 0 ? (
        <ul className="space-y-2">
          {probleme.map((p) => (
            <li
              key={p}
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm leading-relaxed text-red-800"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {p}
            </li>
          ))}
        </ul>
      ) : (
        <p className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          Keine Auffälligkeiten.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Karte icon={Database} titel="Datenbank">
          <Zeile label="Erreichbar" wert={data.datenbank.erreichbar ? 'ja' : 'nein'} warnung={!data.datenbank.erreichbar} />
          <Zeile label="Version" wert={data.datenbank.version ?? '–'} />
          <Zeile label="Größe" wert={bytes(data.datenbank.groesseBytes)} />
          <Zeile
            label="Verbindungen"
            wert={data.datenbank.verbindungen === null ? '–' : zahl(data.datenbank.verbindungen)}
          />
        </Karte>

        <Karte icon={HardDrive} titel="Ablage">
          <Zeile label="Pfad" wert={data.ablage.pfad} mono />
          <Zeile
            label="Beschreibbar"
            wert={data.ablage.beschreibbar ? 'ja' : 'nein'}
            warnung={!data.ablage.beschreibbar}
          />
          <Zeile label="Dateien" wert={zahl(data.ablage.dateien)} />
          <Zeile label="Belegt" wert={bytes(data.ablage.bytes)} />
        </Karte>

        <Karte icon={Cpu} titel="Laufzeit">
          <Zeile label="Node" wert={data.node} />
          <Zeile label="Umgebung" wert={data.umgebung} />
          <Zeile label="Rechnername" wert={data.hostname} mono />
          <Zeile label="Läuft seit" wert={dauer(data.laufzeitSekunden)} />
          <Zeile label="Speicher (Prozess)" wert={bytes(data.speicher.rssBytes)} />
          <Zeile
            label="Speicher (System)"
            wert={`${bytes(data.speicher.systemGesamtBytes - data.speicher.systemFreiBytes)} von ${bytes(data.speicher.systemGesamtBytes)}`}
          />
        </Karte>

        <Karte icon={Archive} titel="Sicherung">
          <Zeile
            label="Storage Box"
            wert={data.sicherung.hetznerKonfiguriert ? 'eingerichtet' : 'nicht eingerichtet'}
          />
          <Zeile
            label="Zuletzt"
            wert={zeitpunkt(data.sicherung.letzterLauf)}
            warnung={data.sicherung.letzterStatus === 'error'}
          />
          <Zeile
            label="Ergebnis"
            wert={
              data.sicherung.letzterStatus === 'success'
                ? 'erfolgreich'
                : data.sicherung.letzterStatus === 'error'
                  ? 'fehlgeschlagen'
                  : '–'
            }
            warnung={data.sicherung.letzterStatus === 'error'}
          />
          <Zeile label="Als Nächstes" wert={zeitpunkt(data.sicherung.naechsterLauf)} />
        </Karte>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <XCircle className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Fehlgeschlagene Aktionen (24 Stunden)
        </h2>
        {data.letzteFehler.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">Nichts protokolliert.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {data.letzteFehler.map((f, i) => (
              <li
                key={`${f.action}-${f.createdAt}-${i}`}
                className="flex flex-wrap items-baseline gap-x-2 text-sm"
              >
                <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                  {f.action}
                </code>
                <span className="min-w-0 truncate text-slate-600">{f.userEmail ?? 'System'}</span>
                <span className="ml-auto shrink-0 text-xs text-slate-400">
                  {zeitpunkt(f.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Was der Betreiber sofort wissen muss – in Worten, nicht als Zahlenrätsel. */
function sammleProbleme(d: Systemzustand): string[] {
  const p: string[] = [];
  if (!d.datenbank.erreichbar) {
    p.push('Die Datenbank ist nicht erreichbar. Ohne sie funktioniert nichts.');
  }
  if (!d.ablage.beschreibbar) {
    p.push(
      `Die Ablage unter ${d.ablage.pfad} ist nicht beschreibbar – Uploads schlagen fehl. Rechte oder Einhängepunkt prüfen.`,
    );
  }
  if (d.sicherung.letzterStatus === 'error') {
    p.push(
      `Die letzte automatische Sicherung ist fehlgeschlagen (${zeitpunkt(d.sicherung.letzterLauf)}). Bis das behoben ist, gibt es keinen aktuellen Stand.`,
    );
  }
  if (!d.sicherung.hetznerKonfiguriert) {
    p.push(
      'Es ist keine Storage Box eingerichtet. Sicherungen liegen dann nur auf demselben Server wie die Daten.',
    );
  }
  return p;
}

function Karte({
  icon: Icon,
  titel,
  children,
}: {
  icon: typeof Database;
  titel: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <Icon className="h-4 w-4 text-slate-400" aria-hidden="true" />
        {titel}
      </h2>
      <dl className="mt-3 grid grid-cols-[max-content,1fr] gap-x-4 gap-y-2 text-sm">{children}</dl>
    </section>
  );
}

function Zeile({
  label,
  wert,
  warnung,
  mono,
}: {
  label: string;
  wert: string;
  warnung?: boolean;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd
        className={`min-w-0 break-all ${mono ? 'font-mono text-xs' : ''} ${
          warnung ? 'font-medium text-red-700' : 'text-slate-800'
        }`}
      >
        {wert}
      </dd>
    </>
  );
}

export default SystemPage;
